// src/components/CommentThread.tsx

import React, { useState, useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
    Comment as CommentType,
    postComment,
    updateComment,
    deleteComment,
} from '../api/comments';
import MentionInput from './MentionInput';
import QuickReactionBar from './QuickReactionBar';
import ReactionBar from './ReactionBar';

type CommentThreadProps = {
    comments: CommentType[];
    setComments: React.Dispatch<React.SetStateAction<CommentType[]>>;
    parentId?: string | null;
    level?: number;
    currentUserEmail: string;
    allUsers: AppUser[];
};

type AppUser = {
    email: string;
};

const CommentThread: React.FC<CommentThreadProps> = ({
    comments,
    setComments,
    parentId = null,
    level = 0,
    currentUserEmail,
    allUsers,
}) => {
    const formatDate = (ts: string) => {
        const iso = ts.endsWith('Z') ? ts : `${ts}Z`;
        return new Date(iso).toLocaleString();
    };

    const EDIT_THRESHOLD = 1000;

    const [collapsedMap, setCollapsedMap] = useState<{ [id: string]: boolean }>(
        {}
    );
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [replyContent, setReplyContent] = useState('');
    const [editContent, setEditContent] = useState('');
    const [reactionRefreshKey, setReactionRefreshKey] = useState<{
        [id: string]: number;
    }>({});
    const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(
        null
    );

    const countDescendants = (cid: string): number => {
        const children = comments.filter((c) => c.parent_id === cid);
        return children.reduce(
            (sum, ch) => sum + 1 + countDescendants(ch.id),
            0
        );
    };

    const topLevelComments = comments
        .filter((c) => c.parent_id === parentId)
        .sort(
            (a, b) =>
                level === 0
                    ? new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime() // newest first
                    : new Date(a.created_at).getTime() -
                      new Date(b.created_at).getTime() // oldest first
        );

    const handleReply = async (comment: CommentType) => {
        if (!replyContent.trim()) return;

        const newReply = await postComment(
            comment.revision_group_id,
            replyContent,
            comment.id
        );
        setComments((p) => [...p, newReply]);
        setReplyingTo(null);
        setReplyContent('');
        setCollapsedMap((m) => ({ ...m, [comment.id]: false }));
    };

    const handleSaveEdit = async (comment: CommentType) => {
        if (!editContent.trim()) return;

        const updated = await updateComment(comment.id, editContent);
        setComments((p) => p.map((c) => (c.id === updated.id ? updated : c)));
        setEditing(null);
        setEditContent('');
    };

    const handleDelete = async (id: string) => {
        const deleted = await deleteComment(id);
        setComments((p) => p.map((c) => (c.id === deleted.id ? deleted : c)));
    };

    const handleReacted = (commentId: string) => () => {
        setReactionRefreshKey((prev) => ({
            ...prev,
            [commentId]: (prev[commentId] ?? 0) + 1,
        }));
    };

    useEffect(() => {
        if (editing) {
            const comment = comments.find((c) => c.id === editing);
            if (comment) {
                setEditContent(comment.content);
            }
        }
    }, [editing, comments]);

    return (
        <Stack spacing={0.5} sx={{ ml: 0.75 }}>
            {topLevelComments.map((comment) => {
                const isOwner = comment.user_email === currentUserEmail;
                const ageMs =
                    Date.now() -
                    new Date(formatDate(comment.created_at)).getTime();
                const withinWindow = ageMs < 60 * 60 * 1000;
                const canModify = isOwner && withinWindow;

                const children = comments.filter(
                    (c) => c.parent_id === comment.id
                );
                const totalDesc = countDescendants(comment.id);
                const isTopLevel = level === 0;
                const isCollapsed = collapsedMap[comment.id] ?? true;
                const showChildren = !isCollapsed || !isTopLevel;

                const toggle = () =>
                    setCollapsedMap((m) => ({
                        ...m,
                        [comment.id]: !isCollapsed,
                    }));

                const created = new Date(comment.created_at).getTime();
                const updated = new Date(comment.updated_at).getTime();
                const wasEdited = updated - created > EDIT_THRESHOLD;

                return (
                    <Box key={comment.id} sx={{ position: 'relative' }}>
                        <Box
                            sx={{
                                borderLeft: '2px solid #ccc',
                                pl: 2,
                                mt: 0.5,
                            }}
                        >
                            {comment.is_deleted ? (
                                <>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {formatDate(comment.created_at)} by{' '}
                                        {comment.user_email}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontStyle="italic"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        This comment has been deleted
                                    </Typography>
                                    {isTopLevel && totalDesc > 0 && (
                                        <Button
                                            size="small"
                                            variant="text"
                                            sx={{
                                                textTransform: 'lowercase',
                                                color: 'text.secondary',
                                            }}
                                            onClick={toggle}
                                        >
                                            {isCollapsed
                                                ? `expand ${totalDesc} repl${totalDesc > 1 ? 'ies' : 'y'}`
                                                : 'collapse'}
                                        </Button>
                                    )}
                                </>
                            ) : editing === comment.id ? (
                                <Box>
                                    <MentionInput
                                        value={editContent}
                                        onChange={setEditContent}
                                        allUsers={allUsers}
                                        onSubmit={() => handleSaveEdit(comment)}
                                        onCancel={() => {
                                            setEditing(null);
                                            setEditContent('');
                                        }}
                                        autoFocus
                                    />
                                    <Box
                                        sx={{ display: 'flex', gap: 1, mt: 1 }}
                                    >
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => {
                                                setEditing(null);
                                                setEditContent('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() =>
                                                handleSaveEdit(comment)
                                            }
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            display: 'inline-block',
                                            maxWidth: '100%',
                                        }}
                                        onMouseEnter={() =>
                                            setHoveredCommentId(comment.id)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredCommentId(null)
                                        }
                                    >
                                        {/* Emoji bar above timestamp */}
                                        {hoveredCommentId === comment.id && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    transform:
                                                        'translateY(-100%)',
                                                    zIndex: 2,
                                                }}
                                            >
                                                <QuickReactionBar
                                                    commentId={comment.id}
                                                    onReacted={handleReacted(
                                                        comment.id
                                                    )}
                                                />
                                            </Box>
                                        )}

                                        {/* Timestamp and author */}
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 0.5 }}
                                        >
                                            {formatDate(comment.created_at)} by{' '}
                                            {comment.user_email}
                                            {!comment.is_deleted &&
                                                wasEdited && <em> (edited)</em>}
                                        </Typography>

                                        {/* Comment text */}
                                        <Box
                                            sx={{
                                                '& .mention-pill': {
                                                    backgroundColor: '#d1c4e9',
                                                    padding: '2px 6px',
                                                    borderRadius: '12px',
                                                    color: '#4a148c',
                                                    fontWeight: 500,
                                                    marginRight: '2px',
                                                },
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: comment.content,
                                            }}
                                        />
                                    </Box>

                                    <ReactionBar
                                        commentId={comment.id}
                                        currentUserEmail={currentUserEmail}
                                        refreshTrigger={
                                            reactionRefreshKey[comment.id]
                                        }
                                    />
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ mb: 1 }}
                                    >
                                        {level < 5 &&
                                            replyingTo !== comment.id && (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    sx={{
                                                        textTransform:
                                                            'lowercase',
                                                        color: 'text.secondary',
                                                    }}
                                                    onClick={() => {
                                                        setReplyingTo(
                                                            comment.id
                                                        );
                                                        setEditing(null);
                                                        setReplyContent('');
                                                    }}
                                                >
                                                    reply
                                                </Button>
                                            )}
                                        {isTopLevel && totalDesc > 0 && (
                                            <Button
                                                size="small"
                                                variant="text"
                                                sx={{
                                                    textTransform: 'lowercase',
                                                    color: 'text.secondary',
                                                }}
                                                onClick={toggle}
                                            >
                                                {isCollapsed
                                                    ? `expand ${totalDesc} repl${totalDesc > 1 ? 'ies' : 'y'}`
                                                    : 'collapse'}
                                            </Button>
                                        )}
                                        {canModify && (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    sx={{
                                                        textTransform:
                                                            'lowercase',
                                                        color: 'text.secondary',
                                                    }}
                                                    onClick={() => {
                                                        setEditing(comment.id);
                                                        setReplyingTo(null);
                                                    }}
                                                >
                                                    edit
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    sx={{
                                                        textTransform:
                                                            'lowercase',
                                                        color: 'text.secondary',
                                                    }}
                                                    onClick={() =>
                                                        handleDelete(comment.id)
                                                    }
                                                >
                                                    delete
                                                </Button>
                                            </>
                                        )}
                                    </Stack>

                                    {replyingTo === comment.id && level < 5 && (
                                        <Box sx={{ mb: 1 }}>
                                            <MentionInput
                                                value={replyContent}
                                                onChange={setReplyContent}
                                                allUsers={allUsers}
                                                onSubmit={() =>
                                                    handleReply(comment)
                                                }
                                                onCancel={() => {
                                                    setReplyingTo(null);
                                                    setReplyContent('');
                                                }}
                                                autoFocus
                                            />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    mt: 1,
                                                }}
                                            >
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                    onClick={() => {
                                                        setReplyingTo(null);
                                                        setReplyContent('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() =>
                                                        handleReply(comment)
                                                    }
                                                >
                                                    Comment
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}
                            {showChildren && children.length > 0 && (
                                <CommentThread
                                    comments={comments}
                                    setComments={setComments}
                                    parentId={comment.id}
                                    level={level + 1}
                                    currentUserEmail={currentUserEmail}
                                    allUsers={allUsers}
                                />
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
};

export default CommentThread;
